import React, { useRef } from "react";
import { Send, Paperclip } from "lucide-react";
import FilePreview from "./FilePreview";

interface MessageInputProps {
  message: string;
  setMessage: (message: string) => void;
  onSend: () => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedFile: File | null;
  onRemoveFile: () => void;
  isConnected: boolean;
  uploading: boolean;
  placeholder?: string;
  attachButtonVariant?: "primary" | "secondary";
  disabled?: boolean; // Added this prop
}

const MessageInput: React.FC<MessageInputProps> = ({
  message,
  setMessage,
  onSend,
  onFileSelect,
  selectedFile,
  onRemoveFile,
  isConnected,
  uploading,
  placeholder = "Message",
  attachButtonVariant = "primary",
  disabled = false, // Default to false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const attachButtonClass =
    attachButtonVariant === "primary"
      ? "bg-blue-50 hover:bg-gray-200 text-blue-600 hover:text-blue-700"
      : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-700";

  // Check if input should be disabled
  const isDisabled = !isConnected || uploading || disabled;

  return (
    <div className="bg-white border-t border-gray-200">
      <FilePreview selectedFile={selectedFile} onRemove={onRemoveFile} />
      <div className="px-6 py-4">
        <input
          ref={fileInputRef}
          type="file"
          onChange={onFileSelect}
          className="hidden"
          accept="image/*,.pdf,.doc,.docx,.txt,.zip"
        />
        <div className="flex items-center space-x-3">
          <div className="flex-1">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                disabled ? "This conversation is closed" : placeholder
              }
              disabled={isDisabled}
              className="w-full resize-none border border-gray-200 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm placeholder-gray-500 leading-relaxed disabled:bg-gray-50 disabled:cursor-not-allowed"
              rows={1}
              style={{
                minHeight: "52px",
                maxHeight: "120px",
              }}
            />
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isDisabled}
            className={`${attachButtonClass} rounded-2xl transition-colors flex items-center justify-center shadow-sm border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            style={{ minHeight: "52px", minWidth: "52px" }}
          >
            <Paperclip size={20} />
          </button>
          <button
            onClick={onSend}
            disabled={isDisabled || (!message.trim() && !selectedFile)}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-2xl transition-colors flex items-center justify-center shadow-sm"
            style={{ minHeight: "52px", minWidth: "52px" }}
          >
            {uploading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send size={20} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageInput;
